import { useEffect, useState } from "react";
import {
  decodeTokenRefresh,
  getTokenRefresh,
} from "../../../../../../helpers/token/refreshToken";
import {
  getDriverStore,
  removeDriverStore,
  saveDriverStore,
} from "../../../../../../helpers/store/DriverStore";
import {
  getNotificationRequestStore,
  removeNotificationRequestStore,
} from "../../../../../../helpers/store/NotificationRequestStore";
import { BookingObserver } from "../../domain/BookingObserver";
import { CancelNotificationClient } from "../../domain/CancelNotificationClient";
import { CounterOfferObserver } from "../../domain/CounterOfferObserver";
import { DeleteAllCounterOffers } from "../../domain/DeleteAllCounterOffers";
import { SendNotificationDriver } from "../../domain/SendNotificationDriver";
import { UpdateCounterOffer } from "../../domain/UpdateCounterOffer";
import { DeleteBooking } from "../../domain/DeleteBooking";
import { createBookingCancellationUseCase } from "../../usecases/cancellation/CreateBookingCancellationUseCase";
import { getListCancellationReasonsUseCase } from "../../usecases/cancellation/GetListCancellationReasonsUseCase";
import { UpdateBackupBookingStatusUseCase } from "../../usecases/backup/UpdateBackupBookingStatusUseCase";

const mapCancellationReasons = (reasons) =>
  reasons
    .filter(
      (reason) =>
        reason.active !== false &&
        (!reason.actor_type || reason.actor_type === "CLIENT"),
    )
    .map((reason) => ({
      id: reason.id,
      code: reason.code,
      name: reason.name,
      requiresComment: reason.requires_comment,
    }));

const parseBackupOrderId = (value) => {
  const parsedId = Number(value);
  return Number.isFinite(parsedId) && parsedId > 0 ? parsedId : null;
};

export const useBookingOffers = () => {
  const [bookingNow, setBookingNow] = useState(null);
  const [riderId, setRiderId] = useState(null);
  const [uidBooking, setUidBooking] = useState("");
  const [isBookingActive, setIsBookingActive] = useState(false);
  const [driverSelect, setDriverSelect] = useState(null);
  const [openOffers, setOpenOffers] = useState(false);
  const [offersList, setOfferList] = useState([]);
  const [cancellationReasons, setCancellationReasons] = useState([]);

  const handleOpenOffer = () => setOpenOffers(true);
  const handleCloseOffer = () => setOpenOffers(false);
  const bookingId =
    bookingNow?.booking_id ??
    bookingNow?.bookingId ??
    bookingNow?.id ??
    bookingNow?.counterBooking ??
    "";
  const bookingOfferDetail = {
    uuid: bookingNow?.uuid ?? "",
    bookingId,
    backupOrderId: bookingNow?.pedido ?? bookingNow?.counterBooking ?? "",
    counterBooking: bookingNow?.counterBooking ?? "",
    pedido: bookingNow?.counterBooking ?? "",
    idDriver:
      bookingNow?.idDriver ??
      bookingNow?.driverId ??
      bookingNow?.driver_id ??
      "",
    driverName:
      bookingNow?.driverName ??
      bookingNow?.nameDriver ??
      bookingNow?.driver?.name ??
      "",
    driverArrivalTime:
      bookingNow?.timeArrived ??
      bookingNow?.driverArrivalTime ??
      bookingNow?.driver?.time ??
      "",
    driverPlate:
      bookingNow?.plate ??
      bookingNow?.driverPlate ??
      bookingNow?.driver?.plate ??
      "",
    driverModel:
      bookingNow?.model ??
      bookingNow?.driverModel ??
      bookingNow?.driver?.model ??
      "",
    driverColor:
      bookingNow?.color ??
      bookingNow?.driverColor ??
      bookingNow?.driver?.color ??
      "",
    statusService: bookingNow?.statusService ?? "",
    clientName: bookingNow?.clientName ?? "",
    fecha: bookingNow?.date ?? "",
    hora: bookingNow?.time ?? "",
    otherPersonName: bookingNow?.otherPersonName ?? "",
    otherPersonPhone: bookingNow?.otherPersonPhone ?? "",
    origen: bookingNow?.origin?.name ?? "",
    subRoutes: Array.isArray(bookingNow?.subRoutes)
      ? bookingNow.subRoutes
      : bookingNow?.subRutes ?? [],
    destino: bookingNow?.destination?.name ?? "",
    notas: bookingNow?.note ?? bookingNow?.notes ?? "",
    total: bookingNow?.total ?? bookingNow?.total ?? "",
    statusBefore:
      bookingNow?.statusBooking ??
      bookingNow?.statusService ??
      bookingNow?.status ??
      "",
  };

  useEffect(() => {
    const token = getTokenRefresh();
    if (!token) return undefined;

    const uid = decodeTokenRefresh(token);
    setRiderId(uid);

    const unsubscribe = BookingObserver(uid, (bookingList) => {
      if (bookingList.length > 0) {
        const activeBooking = bookingList[0];
        setBookingNow(activeBooking);
        setIsBookingActive(true);
        setUidBooking(activeBooking.uuid);
        const driver = getDriverStore();
        if (driver != null) {
          setDriverSelect(driver);
        }
      } else {
        handleCloseOffer();
        setBookingNow(null);
        setUidBooking("");
        setOfferList([]);
        setIsBookingActive(false);
        removeDriverStore();
        removeNotificationRequestStore();
      }
    });

    return () => {
      if (unsubscribe) unsubscribe();
    };
  }, []);

  useEffect(() => {
    const loadCancellationReasons = async () => {
      try {
        const response = await getListCancellationReasonsUseCase();
        setCancellationReasons(
          mapCancellationReasons(response?.data?.data ?? []),
        );
      } catch (error) {
        console.log(error);
        setCancellationReasons([]);
      }
    };

    loadCancellationReasons();
  }, []);

  useEffect(() => {
    if (!riderId || uidBooking === "") {
      setOfferList([]);
      return undefined;
    }

    const unsubscribe = CounterOfferObserver(riderId, uidBooking, (offers) => {
      setOfferList(offers);
    });

    return () => {
      if (unsubscribe) unsubscribe();
    };
  }, [riderId, uidBooking]);

  const acceptCounterOffer = async (offer) => {
    const idBooking = offer.idBooking;
    const idDriver = offer.idDriver;
    const idRider = offer.idRider;
    const price = offer.priceOffer;
    const driver = {
      driverName: offer.nameDriver,
      driverPhone: offer.phoneDriver,
      vehicleId: offer.vehicleId,
      brand: offer.brand,
      model: offer.model,
      bodywork: offer.bodywork,
      plate: offer.plate,
      color: offer.color,
      time: offer.timeArrived,
    };
    saveDriverStore(driver);
    setDriverSelect(driver);
    await UpdateCounterOffer(idBooking, idDriver, price);
    await SendNotificationDriver(idDriver);
    await DeleteAllCounterOffers(idRider, idBooking);
  };

  const cancelBooking = async (
    idBooking,
    cancellationReasonId,
    backupOrderId,
  ) => {
    const parsedBackupOrderId = parseBackupOrderId(
      backupOrderId ?? bookingOfferDetail.backupOrderId,
    );

    if (!parsedBackupOrderId) {
      throw new Error("No se encontro el id del backup_order para cancelar");
    }

    await createBookingCancellationUseCase({
      booking_id: Number(bookingOfferDetail.bookingId),
      cancelled_by_type: "CLIENT",
      cancelled_by_id: riderId != null ? Number(riderId) : null,
      cancellation_reason_id: Number(cancellationReasonId),
      booking_status_before: bookingOfferDetail.statusBefore || null,
      cancellation_fee: 0,
    });

    const backupStatusResponse = await UpdateBackupBookingStatusUseCase({
      id: parsedBackupOrderId,
      bookingStatus: "CANCELADO",
    });

    if (backupStatusResponse?.message !== "success") {
      throw new Error("No se pudo actualizar el estado del backup_order");
    }

    const notificationRequest = getNotificationRequestStore();
    if (notificationRequest?.requestId && notificationRequest?.orderNumber) {
      CancelNotificationClient(notificationRequest);
      removeNotificationRequestStore();
    }

    await DeleteBooking(idBooking);
  };

  return {
    bookingOfferDetail,
    isBookingActive,
    driverSelect,
    openOffers,
    offersList,
    cancellationReasons,
    handleOpenOffer,
    handleCloseOffer,
    acceptCounterOffer,
    cancelBooking,
  };
};
